<template>
    <div>
        <PageHeader>
            {{ $t('widgetVersionReport') }}
            <template #side>
                <div class="d-flex gap-4">
                    <MultiselectSpaces
                        :disabled="loading"
                        :placeholder="$t('space')"
                        class="width-min-250"
                        multiple
                        show-checkboxes
                        :close-on-select="false"
                        @inputTrackBy="spaces = $event"
                        @change="fetchData()"
                    />
                    <TimeRangeCalendar v-model="timeRange" @timeRange="fetchData()" />
                </div>
            </template>
        </PageHeader>

        <div class="d-flex flex-column gap-4">

            <div class="d-flex flex-wrap gap-4">
                <EChart
                    card
                    class="m-0 p-0 flex-basis-0 flex-grow-10 width-min-400"
                    :header="$t('widgetVersionImpressionsChart')"
                    :loading="loading"
                    :error="requestChart.getError()"
                    :options="chartOptions"
                    type="time"
                    :height="420"
                    :group-by="groupBy"
                />
                <EChart
                    card
                    class="mb-0 width-min-500 flex-basis-0 flex-grow-1"
                    :header="$t('widgetVersionShare')"
                    :loading="loading"
                    :error="requestChart.getError()"
                    :options="pieChartOptions"
                    :height="420"
                />
            </div>

            <DataTable
                :header="$t('widgetVersionSitesList')"
                :error="requestChart.getError()"
                :items="tableItems"
                :total-rows="tableItems.length"
                :loading="loading"
                :fields="tableFields"
            />

        </div>

    </div>
</template>

<script>
    import { generateEChartData, chartDataGroupBy } from '@/utils/chart';
    import PageHeader from '@/components/common/PageHeader.vue';
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import EChart from '@/components/common/EChart.vue';
    import MultiselectSpaces from '@/components/common/MultiselectSpaces.vue';

    export default {
        name: 'ReportsWidgetVersionChart',
        components: {
            PageHeader,
            TimeRangeCalendar,
            DataTable,
            EChart,
            MultiselectSpaces,
        },
        data() {
            return {
                requestChart: this.$req('userStats/WidgetVersionChart'),
                timeRange: null,
                versionsOptions: ['latest', '5.0', '5.1', '6.0'],
                versions: [],
                spaces: [],
            };
        },
        computed: {
            loading() {
                return this.requestChart.getLoading();
            },
            groupBy() {
                return chartDataGroupBy(this.timeRange);
            },
            chartData() {
                return this.requestChart.getData() || {};
            },
            versionKeys() {
                const raw = this.chartData.chart || [];
                if (!raw.length) return [];
                const keys = new Set();
                raw.forEach(item =>
                    Object.keys(item)
                        .filter(key => key !== 'date')
                        .forEach(key => keys.add(key))
                );
                return Array.from(keys);
            },
            chartOptions() {
                const raw = this.chartData.chart || [];
                const series = this.versionKeys.map(version => {
                    const pairs = raw.map(item => [new Date(item.date).setHours(0, 0, 0, 0), item[version] ?? 0]);
                    const data = generateEChartData(this.timeRange, pairs);
                    return {
                        name: version,
                        data,
                    };
                });
                return {
                    series,
                };
            },
            tableItems() {
                return (this.chartData.pie_chart || []).map(([version, impressions]) => ({ version, impressions }));
            },
            pieChartOptions() {
                const items = this.chartData.pie_chart || [];
                const data = items.map(([name, value]) => ({
                    name,
                    value: Number(Number(value).toFixed(2)),
                }));
                return {
                    series: [{ type: 'pie', data }],
                };
            },
            tableFields() {
                return [
                    { key: 'version', label: this.$t('widgetVersion'), sortable: true },
                    { key: 'impressions', label: this.$t('impressions'), sortable: true },
                ];
            },
            requestParams() {
                return { ...this.timeRange, versions: this.versions, spaces: this.spaces };
            },
        },
        methods: {
            fetchData() {
                if (!this.timeRange) return;
                this.requestChart.reset().get({ requestParams: this.requestParams });
            },
        },
    };
</script>
